/**
 * TrendChart Component
 *
 * Displays a line chart showing historical trends of total execution time
 * across multiple profiling sessions.
 */

import React, {useMemo} from 'react';
import {Box, Typography} from '@mui/material';
import {
    CategoryScale, Chart as ChartJS, Filler, Legend, LinearScale, LineElement, PointElement, Title, Tooltip
} from 'chart.js';
import {Line} from 'react-chartjs-2';
import {ProfileSession} from '../../@types/profiler';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

interface TrendChartProps {
    sessions: ProfileSession[];
    height?: number;
}

const TrendChart: React.FC<TrendChartProps> = ({ sessions, height = 200 }) => {
    const chartData = useMemo(() => {
        // Reverse to show oldest first (chronological order)
        const orderedSessions = [...sessions].reverse();

        const labels = orderedSessions.map((s, i) => {
            // Use session number or timestamp
            const time = new Date(s.timestamp);
            return `#${i + 1} (${time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`;
        });

        const totalTimes = orderedSessions.map(s => s.totalTime);

        // Calculate average
        const average = totalTimes.length > 0
            ? totalTimes.reduce((a, b) => a + b, 0) / totalTimes.length
            : 0;

        return {
            labels,
            datasets: [
                {
                    label: 'Total Time (ms)',
                    data: totalTimes,
                    borderColor: 'rgba(33, 150, 243, 1)',
                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                    fill: true,
                    tension: 0.3,
                    pointRadius: 4,
                    pointHoverRadius: 6,
                    pointBackgroundColor: 'rgba(33, 150, 243, 1)',
                },
                {
                    label: 'Average',
                    data: Array(totalTimes.length).fill(average),
                    borderColor: 'rgba(255, 152, 0, 0.8)',
                    borderDash: [5, 5],
                    pointRadius: 0,
                    fill: false,
                }
            ],
            average
        };
    }, [sessions]);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top' as const,
                labels: {
                    boxWidth: 12,
                    font: {
                        size: 10
                    }
                }
            },
            title: {
                display: true,
                text: 'Performance Trend',
                font: {
                    size: 14,
                    weight: 600 as const
                }
            },
            tooltip: {
                callbacks: {
                    label: (context: { dataset: { label?: string }; parsed: { y: number } }): string => {
                        return `${context.dataset.label || 'Value'}: ${context.parsed.y.toFixed(1)}ms`;
                    },
                    afterBody: (tooltipItems: { dataIndex: number }[]): string[] => {
                        const index = tooltipItems[0]?.dataIndex;
                        const orderedSessions = [...sessions].reverse();
                        const session = orderedSessions[index];
                        if (session) {
                            const lines = [
                                '',
                                `Operation: ${session.contextLabel}`
                            ];
                            if (session.metadata?.elementCount) {
                                lines.push(`Elements: ${session.metadata.elementCount}`);
                            }
                            if (session.metadata?.nodeCount) {
                                lines.push(`Nodes: ${session.metadata.nodeCount}`);
                            }
                            return lines;
                        }
                        return [];
                    }
                }
            }
        },
        scales: {
            x: {
                ticks: {
                    font: {
                        size: 9
                    },
                    maxRotation: 45,
                    minRotation: 45
                }
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Time (ms)',
                    font: {
                        size: 11
                    }
                },
                ticks: {
                    font: {
                        size: 10
                    }
                }
            }
        },
        animation: {
            duration: 300
        },
        interaction: {
            intersect: false,
            mode: 'index' as const
        }
    }), [sessions]);

    if (sessions.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">No historical data available</Typography>
            </Box>
        );
    }

    if (sessions.length === 1) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">
                    Need at least 2 sessions for trend analysis
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Current session: {sessions[0].totalTime.toFixed(1)}ms
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height, p: 1 }}>
            <Line data={chartData} options={options} />
        </Box>
    );
};

export default React.memo(TrendChart);
